import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CARD1 = "https://images.unsplash.com/photo-1783685158053-08213c96da9a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const CARD2 = "https://images.unsplash.com/photo-1763485956292-6fb531f01b0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const CARD3 = "https://images.unsplash.com/photo-1752321531522-20d9c4dc2e0b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const workflow = [
  ["01","Questionnaire","Capture the client's spatial personality, rituals and material sensibility."],
  ["02","Inspiration","Ingest a curated set of reference images with your notes."],
  ["03","AI Analysis","Extract chroma, materiality, form and lighting patterns."],
  ["04","Design DNA","A codified colour, material and keyword system."],
  ["05","Mood Boards","Composed room-by-room, coherent with the direction."],
  ["06","Presentation","Editorial 16:9 deck ready to present or export."],
];

export const FAQ = [
  ["What is an AI mood board generator for interior design?", "Moodboard Narrators reads a client questionnaire and reference images, identifies the recurring visual patterns, and composes a coherent Design DNA — palette, materials, forms and lighting — before generating room-wise mood boards and a client presentation."],
  ["Can architects use it, or only interior designers?", "Both. Choose an Interior or Architecture project at the start; architecture projects add building type, plot area, floors, site context and exterior spaces such as façade, entrance and courtyard."],
  ["How do I present the result to a client?", "Every project produces a 16:9 client presentation with cover, client profile, Design DNA, palette, materials and one slide per room. Export it as PDF or editable PPTX with your studio logo in the corner you choose."],
  ["Does it keep every room consistent with the approved design direction?", "Yes. A Design-DNA consistency check scores each room and flags materials or colours that drift from the approved direction, with a one-line suggestion to fix it."],
  ["Is my client data private?", "Sign-in is mandatory (email or Google) and every project, image and brand setting is private to your studio account."],
];

export default function Landing() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <header className="border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1440px] mx-auto flex items-center justify-between px-6 sm:px-12 py-6">
          <Link to="/" className="flex items-baseline gap-3" data-testid="landing-brand">
            <span className="eyebrow">Atelier</span>
            <span className="font-serif-editorial text-2xl">Moodboard Narrators</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#workflow" className="hover-underline">Method</a>
            <a href="#sample" className="hover-underline">Sample</a>
            <Link to="/login" className="hover-underline" data-testid="landing-nav-dashboard">Sign in</Link>
          </nav>
          <Link to="/project/new" data-testid="landing-cta-header" className="text-sm px-5 py-3 bg-black text-white hover:bg-stone-800 transition-colors">
            Start a Project
          </Link>
        </div>
      </header>

      <section className="max-w-[1440px] mx-auto px-6 sm:px-12 pt-16 sm:pt-24 pb-20">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-6 flex flex-col justify-center">
            <div className="eyebrow mb-6">Vol. 01 — Studio Method</div>
            <h1 className="editorial-heading text-5xl sm:text-6xl lg:text-7xl">
              Turn client<br/>preferences<br/>into complete<br/><em className="font-serif-editorial italic text-[color:var(--terracotta)]">design directions.</em>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-stone-600 max-w-xl leading-relaxed">
              Upload a questionnaire and a handful of inspiration images. Moodboard Narrators analyses your client's taste and composes a coherent, presentation-ready mood board and slide deck for your project.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/project/new" data-testid="landing-cta-primary" className="group inline-flex items-center gap-3 px-6 py-4 bg-black text-white text-sm">
                Create Your First Mood Board <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="#workflow" data-testid="landing-cta-secondary" className="inline-flex items-center gap-3 px-6 py-4 border border-stone-300 text-sm hover-underline">
                See How It Works
              </a>
            </div>
            <div className="mt-14 flex items-center gap-8">
              <div>
                <div className="font-mono-lab text-2xl">7</div>
                <div className="eyebrow mt-1">Reasoning stages</div>
              </div>
              <div className="w-px h-10 bg-stone-200" />
              <div>
                <div className="font-mono-lab text-2xl">3</div>
                <div className="eyebrow mt-1">Direction options</div>
              </div>
              <div className="w-px h-10 bg-stone-200" />
              <div>
                <div className="font-mono-lab text-2xl">16:9</div>
                <div className="eyebrow mt-1">Editorial deck</div>
              </div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-6 relative">
            <div className="aspect-[4/5] overflow-hidden relative">
              <img src={HERO} alt="Editorial living room" className="w-full h-full object-cover" />
              <div className="absolute left-6 bottom-6 bg-white/90 backdrop-blur px-5 py-4 max-w-[280px]">
                <div className="eyebrow mb-2">Sample — Villa Calvi</div>
                <div className="font-serif-editorial text-xl leading-tight">Warm Contemporary Luxury</div>
                <div className="text-xs text-stone-500 mt-2">Honed travertine · Bleached oak · Belgian linen</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="workflow" className="border-t border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1440px] mx-auto px-6 sm:px-12 py-20">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 lg:col-span-5">
              <div className="eyebrow mb-4">The Method</div>
              <h2 className="editorial-heading text-4xl sm:text-5xl">A studio workflow, encoded.</h2>
            </div>
            <div className="col-span-12 lg:col-span-6 lg:col-start-7">
              <p className="text-stone-600 leading-relaxed">
                Moodboard Narrators is not an image generator. It is a design reasoning system — it understands the relationship between client, lifestyle, preferences and references, then translates them into a coherent architectural language.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {workflow.map(([n,t,d]) => (
              <div key={n}>
                <div className="font-mono-lab text-xs mb-4">{n} — Stage</div>
                <div className="hairline mb-5" />
                <h3 className="font-serif-editorial text-2xl mb-3">{t}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="sample" className="max-w-[1440px] mx-auto px-6 sm:px-12 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="eyebrow mb-3">Sample Project</div>
            <h2 className="editorial-heading text-4xl sm:text-5xl">Villa Calvi — Henderson Residence</h2>
          </div>
          <Link to="/dashboard" className="hidden sm:inline-flex items-center gap-2 text-sm hover-underline" data-testid="landing-view-dashboard">
            Open Dashboard <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-6 aspect-[4/3] overflow-hidden"><img src={CARD1} alt="AI-generated living room mood board in warm travertine and oak" loading="lazy" className="w-full h-full object-cover" /></div>
          <div className="col-span-6 md:col-span-3 aspect-square overflow-hidden"><img src={CARD2} alt="Material palette detail — honed stone and linen" loading="lazy" className="w-full h-full object-cover" /></div>
          <div className="col-span-6 md:col-span-3 aspect-square overflow-hidden"><img src={CARD3} alt="Interior design presentation slide sample" loading="lazy" className="w-full h-full object-cover" /></div>
          <div className="col-span-12 mt-6 grid grid-cols-5 gap-4">
            {[["Chalk","#F3EFEA"],["Travertine","#D8CEBE"],["Warm Stone","#A89B89"],["Umber","#544E45"],["Obsidian","#242220"]].map(([n,h]) => (
              <div key={h} className="swatch-chip flex flex-col justify-end p-3" style={{ background: h, color: h==="#242220"||h==="#544E45"?"#F5F5F0":"#1A1A18" }}>
                <div className="eyebrow" style={{ color: "inherit", opacity: 0.7 }}>{h}</div>
                <div className="text-sm font-medium mt-1">{n}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="border-t" style={{ borderColor: "var(--border)" }} aria-labelledby="faq-heading">
        <div className="max-w-[1440px] mx-auto px-6 sm:px-12 py-20 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <div className="eyebrow mb-4">FAQ</div>
            <h2 id="faq-heading" className="editorial-heading text-4xl sm:text-5xl">Questions designers ask us.</h2>
          </div>
          <dl className="col-span-12 lg:col-span-7 lg:col-start-6 divide-y" style={{ borderColor: "var(--border)" }} data-testid="landing-faq">
            {FAQ.map(([q, a]) => (
              <div key={q} className="py-6">
                <dt className="font-serif-editorial text-2xl mb-2">{q}</dt>
                <dd className="text-sm text-stone-600 leading-relaxed">{a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <footer className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1440px] mx-auto px-6 sm:px-12 py-12 grid grid-cols-12 gap-8 text-sm text-stone-500">
          <div className="col-span-12 md:col-span-5">
            <div className="font-serif-editorial text-2xl text-black mb-2">Moodboard Narrators</div>
            <p className="leading-relaxed">AI mood board generator and client presentation software for interior designers and architects. Design DNA, room-wise mood boards, PDF &amp; PPTX export.</p>
          </div>
          <nav className="col-span-6 md:col-span-3 space-y-2" aria-label="Product">
            <div className="eyebrow mb-3">Product</div>
            <a href="#workflow" className="block hover:text-black">How it works</a>
            <a href="#sample" className="block hover:text-black">Sample mood board</a>
            <a href="#faq" className="block hover:text-black">FAQ</a>
          </nav>
          <nav className="col-span-6 md:col-span-3 space-y-2" aria-label="Account">
            <div className="eyebrow mb-3">Studio</div>
            <Link to="/login" className="block hover:text-black">Sign in</Link>
            <Link to="/login" className="block hover:text-black">Create a studio account</Link>
          </nav>
          <div className="col-span-12 flex flex-wrap justify-between gap-4 pt-6 border-t text-xs" style={{ borderColor: "var(--border)" }}>
            <div>© 2026 Moodboard Narrators — AI mood boards for interior design &amp; architecture studios</div>
            <div className="font-mono-lab">Made for designers, by designers</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

